import { z } from "zod";
import { ProductionRoleSchema } from "./parameters-counter.js";

export const MachineTypeSchema = z.enum(["MACHINE", "EQUIPMENT", "HUMAN"]);

export const CreateMachineRequestSchema = z.object({
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  type: MachineTypeSchema,
  requiresOperator: z.boolean().default(false),
  stoppedCostPerHour: z.number().nonnegative().optional(),
  // Spec 080 — só o Update tinha o campo, então a CRIAÇÃO avulsa de máquina descartava o modelo 3D
  // pelo mesmo motivo que o rascunho: quem escolhia o modelo ao criar recebia um bloco genérico.
  model3dId: z.string().uuid().nullable().optional(),
});
export type CreateMachineRequest = z.infer<typeof CreateMachineRequestSchema>;

// StoppedCostPerHour aceita null no update para permitir LIMPAR o custo
// (campo vazio no form persiste como NULL; antes o clear era silenciosamente ignorado).
export const UpdateMachineRequestSchema = CreateMachineRequestSchema.partial().extend({
  stoppedCostPerHour: z.number().nonnegative().nullable().optional(),
  // Spec 078 — modelo 3D da biblioteca (null desassocia ⇒ bloco genérico na cena).
  model3dId: z.string().uuid().nullable().optional(),
});
export type UpdateMachineRequest = z.infer<typeof UpdateMachineRequestSchema>;

// Duplicar máquina: clona escalares + parâmetros + compatibilidade. Nome
// opcional sobrescreve o da cópia (default: nome do original). O código único é gerado no service.
export const DuplicateMachineRequestSchema = z
  .object({ name: z.string().min(1).max(120).optional() })
  .strict();
export type DuplicateMachineRequest = z.infer<typeof DuplicateMachineRequestSchema>;

// Contrato de LEITURA de máquina (GET /lines/:lineId/machines), fonte única do
// tipo MachineDetail do frontend (antes FE-local e divergente do wire). Usado como fonte de
// tipo (z.infer); validação de response em runtime permanece off (guardrail #6).
// Nota: stoppedCostPerHour cruza o wire como STRING (Prisma Decimal serializado) quando
// vem da API, mas adapters de draft produzem number — união honesta com os dois produtores.
// Spec 078 — colocação da máquina na cena 3D, em UNIDADES DE CENA (metros), rotação em Y.
// Ausência (null na máquina) significa "segue o canvas 2D"; `0,0,0` é uma posição real.
export const MachinePlacementSchema = z.object({
  x: z.number().finite(),
  z: z.number().finite(),
  rotationDeg: z.number().finite().min(0).lt(360),
});
export type MachinePlacement = z.infer<typeof MachinePlacementSchema>;
export const MachinePlacementRequestSchema = MachinePlacementSchema.strict();
export type MachinePlacementRequest = z.infer<typeof MachinePlacementRequestSchema>;

export const MachineDetailSensorSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: z.enum(["ONLINE", "OFFLINE", "NO_READING", "INCONSISTENT"]),
  state: z.enum(["AVAILABLE", "LINKED", "NO_READING", "INCONSISTENT"]),
});

export const MachineDetailParameterSchema = z.object({
  id: z.string(),
  name: z.string(),
  unit: z.string().nullable(),
  currentValue: z.string().nullable(),
  source: z.string(),
  influencesStatus: z.boolean(),
  // Spec 081 — o SENTIDO substitui o booleano da Spec 043. O FE deriva daqui tanto "a linha é
  // medida por máquina" (qualquer papel) quanto "aceita apontamento de produção" (só PRODUCES) —
  // duas perguntas diferentes que o booleano respondia como se fossem uma (FR-018/FR-018a).
  productionRole: ProductionRoleSchema.nullable(),
  // Spec 078 — papel visual na cena + regra de alarme por limite (Decimal cruza o wire como string).
  visualRole: z.enum(["NONE", "FLOW", "TEMPERATURE"]).optional(),
  limitMax: z.union([z.string(), z.number()]).nullable().optional(),
  limitMin: z.union([z.string(), z.number()]).nullable().optional(),
  sensorLinks: z.array(z.object({ id: z.string(), sensor: MachineDetailSensorSchema })).optional(),
});

export const MachineDetailSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  // Spec 081 — a unidade de produção da linha a que a máquina pertence. Vive aqui porque toda tela
  // que mostra um número desta máquina precisa do rótulo dele, e não vale uma consulta extra.
  lineProductionUnit: z.string().optional(),
  type: MachineTypeSchema,
  status: z.enum(["RUNNING", "STOPPED", "IDLE", "NODATA"]),
  statusSince: z.string().nullable(),
  influencesStatus: z.boolean(),
  requiresOperator: z.boolean(),
  stoppedCostPerHour: z.union([z.string(), z.number()]).nullable(),
  parameters: z.array(MachineDetailParameterSchema).optional(),
  // Spec 078 — ponteiro para o modelo 3D e colocação própria na cena (null = segue o canvas 2D).
  // Opcionais no schema porque adapters de draft (linha em rascunho) não os produzem.
  model3dId: z.string().uuid().nullable().optional(),
  placement: MachinePlacementSchema.nullable().optional(),
});
export type MachineDetail = z.infer<typeof MachineDetailSchema>;

export const ListMachineEventsQuerySchema = z
  .object({
    machineId: z.string().uuid().optional(),
    // Review item 6 — filtro por linha (evita o fan-out 1-request-por-máquina no card de eventos).
    lineId: z.string().uuid().optional(),
    // Janela de período (inclusiva) para o histórico.
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(20),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: "Período inválido: 'from' deve ser anterior ou igual a 'to'",
    path: ["to"],
  });
export type ListMachineEventsQuery = z.infer<typeof ListMachineEventsQuerySchema>;

// Forma canônica ÚNICA do evento de máquina no wire (GET /machine-events lista + PATCH justify +
// POST acknowledge). Superset consumido por DUAS views de FE, ambas via z.infer (Guardrail #2 — sem
// tipo à mão): o card "Eventos recentes" da linha e a sidebar da máquina consomem ESTE mesmo tipo
// (MachineEventSummary). machineName/machineCode vêm aninhados via include do `machine` (mapeados por
// `withMachineLabel` no API). Enums espelham os Prisma (EventType/EventOrigin/StopClass/ReasonStatus).
// NÃO ligar como `schema.response`: validação de response é off por padrão (Guardrail #6).
export const MachineEventSummarySchema = z.object({
  id: z.string(),
  machineId: z.string(),
  machineName: z.string(),
  machineCode: z.string(),
  type: z.enum(["STOP", "START", "ALERT", "MAINTENANCE", "NODATA"]),
  origin: z.enum(["SENSOR", "MANUAL", "IMPORT", "INTEGRATION"]),
  reason: z.string().nullable(),
  reasonSlug: z.string().nullable(),
  reasonOtherDesc: z.string().nullable(),
  reasonStatus: z.enum(["PENDING", "JUSTIFIED"]),
  acknowledgedAt: z.string().nullable(),
  acknowledgedBy: z.string().nullable(),
  justifiedAt: z.string().nullable(),
  startAt: z.string(),
  endAt: z.string().nullable(),
  durationMinutes: z.number().nullable(),
  stopClass: z.enum(["WITH_ALARM", "WITHOUT_ALARM", "INFORMATIVE"]).nullable(),
});
export type MachineEventSummary = z.infer<typeof MachineEventSummarySchema>;

export const JustifyMachineEventRequestSchema = z.object({
  reasonSlug: z
    .string()
    .min(1, "Motivo obrigatório")
    .regex(/^[a-z][a-z0-9_]*$/, "slug inválido"),
  reasonOtherDesc: z.string().max(500).optional(),
});
export type JustifyMachineEventRequest = z.infer<typeof JustifyMachineEventRequestSchema>;

export const AcknowledgeMachineEventRequestSchema = z.object({}).strict();
export type AcknowledgeMachineEventRequest = z.infer<typeof AcknowledgeMachineEventRequestSchema>;

// Vínculo de máquina semelhante p/ cold-start tier 2 (null limpa).
export const SetSimilarMachineRequestSchema = z.object({
  similarMachineId: z.string().uuid().nullable(),
});
export type SetSimilarMachineRequest = z.infer<typeof SetSimilarMachineRequestSchema>;

// ── Acervo de máquinas org-wide (GET /api/v1/machines) ───────────────
// Seletor "adicionar máquina" do sandbox: todas as linhas da organização. Leitura pura.
export const ListOrgMachinesQuerySchema = z.object({
  search: z.string().max(120).optional(), // filtra por code/name
  compatibleWithProductId: z.string().uuid().optional(), // pré-filtra acervo compatível (evita 409)
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListOrgMachinesQuery = z.infer<typeof ListOrgMachinesQuerySchema>;

export const OrgMachineItemSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  type: MachineTypeSchema,
  status: z.string(),
  lineId: z.string().uuid(),
  lineName: z.string(), // contexto de origem no seletor
  hasMeasuredHistory: z.boolean(), // tier 1 do cold-start disponível (badge de confiança)
});
export type OrgMachineItem = z.infer<typeof OrgMachineItemSchema>;

export const ListOrgMachinesResponseSchema = z.object({
  items: z.array(OrgMachineItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type ListOrgMachinesResponse = z.infer<typeof ListOrgMachinesResponseSchema>;
